import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import APCompleteDialog from "../../custom/APCompleteDialog";
import APResultDialog from "../../custom/APResultDialog";
import APCommonDialog from "../../components/modals/APCommonDialog";
import InitialStep from "./components/InitialStep";
import ValidationStatus from "./components/ValidationStatus";
import CorrectnessModal from "./components/CorrectnessModal";
import ExerciseScreen from "./components/ExerciseScreen";
import Profile from "../Profile";
import ScenarioListScreen from "../ScenarioScreen";
import "../../App.css";
import { useQuery } from "../../custom/useQuery";
import { Box, Flex, Grid, Text } from "@chakra-ui/react";
import { resolveWordList, saveToMyList } from "../../utils";
import { useSettingsContext } from "../../contexts/SettingsContext";
import TextWithLineBreaks from "../../components/TextWithLineBreaks";
import { ErrorTransModel, SquidexScenarioData, WordItem } from "ap-exerciser-sdk/models";
import { MAINSCREEN_RESULT_STEPS } from "../../enums/MainApp";
import { IExerciserSDK } from "ap-exerciser-sdk";
import { StatBox } from "../Profile/components/StatBox";
import ScenarioPreviewModal from "./components/ScenarioPreviewModal";
import { VocabScenario, VocabWord } from "../../VocabModel";
import { useVocabIntl } from "../../hooks/useVocabTranslation";
import { guestScenariosStorageKey } from "../../constants/vocabulaire";

interface MainScreenProps {
  sdk?: IExerciserSDK;
  mappedWordList: Map<string, VocabWord[]>;
  mappedIdWordList: Map<string, VocabWord[]>;
}

function MainScreen({ sdk, mappedWordList, mappedIdWordList }: MainScreenProps) {
  const [exerciseWords, setExerciseWords] = useState<VocabWord[]>([]);
  const scenarioWords = useRef<VocabWord[]>();
  const scenarioAuthor = useRef<string>();
  const [tags, setTags] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [scenarioId, setScenarioId] = useState("");
  const [step, setStep] = useState(0);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [correct, setCorrect] = useState(0);
  const [wordValue, setWordValue] = useState<string>("");
  const [error, setError] = useState<ErrorTransModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [fail, setFail] = useState(false);
  const [pass, setPass] = useState(false);
  const [preview, setPreview] = useState(false);
  const [rows, setRows] = useState(1);
  const [wordcode, setWordcode] = useState("");
  const [exitExercise, setExitExercise] = useState(false);
  const [restartMode, setRestartMode] = useState<boolean>(false);
  const [ignoreStats, setIgnoreStats] = useState<boolean>(false);
  const [result, setResult] = useState<MAINSCREEN_RESULT_STEPS>(
    MAINSCREEN_RESULT_STEPS.INITIAL
  );
  const [currentScenario, setCurrentScenario] = useState<VocabScenario | null>(null);

  const [correctWords, setCorrectWords] = useState<VocabWord[]>([]);
  const [wrongWords, setWrongWords] = useState<VocabWord[]>([]);
  const [passedWords, setPassedWords] = useState<VocabWord[]>([]);
  const [progress, setProgress] = useState(0);
  const [initialWordsLength, setInitialWordsLength] = useState(0);
  const [audioPath, setAudioPath] = useState("");
  const [wrongPassed, setWrongPassed] = useState(false);
  const [listMode, setListMode] = useState("");
  const [displayWord, setDisplayWord] = useState("");
  const [demo, setDemo] = useState("");
  const [annualExerciseCount, setAnnualExerciseCount] = useState<number>(0);
  const [annualWordList, setAnnualWordList] = useState<string[]>([]);
  const [sessionExerciseCount, setSessionExerciseCount] = useState<number>(0)
  const [sessionWordList, setSessionWordList] = useState<string[]>([])
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

  const { getFormattedString, APFormattedMessage } = useVocabIntl();


  const navigate = useNavigate();
  const query = useQuery();

  const { skin } = useSettingsContext();

  useEffect(() => {
    const handleSessionData = async () => {
      try {
        if (sdk) {
          const sessionData = await sdk.getSessionManager().getSessionData();
          if (sessionData.sessionExerciseCount === 0) {
            setSessionExerciseCount(0);
          } else {
            setSessionExerciseCount(sessionData.sessionExerciseCount);
          }
          if (sessionData.sessionWordList.length === 0) {
            setSessionWordList([]);
          } else {
            setSessionWordList(sessionData.sessionWordList);
          }
        }
      } catch (err) {}
    }
    if (sdk) {
      setIsLoggedIn(sdk._firebase.isLoggedIn());
      handleSessionData();
    }
  }, [sdk, sessionExerciseCount]);

  useEffect(() => {
    if (sdk && isLoggedIn) {
      const getUserSettings = async () => {
        try {
          const user = await sdk.getDataManager().getSettings();
          if (user && user.annualExerciseCount) {
            setAnnualExerciseCount(user.annualExerciseCount);
          }
          if (user && user.annualWordList) {
            setAnnualWordList(user.annualWordList);
          }
        } catch (err) {}
      }
      getUserSettings();
    }
  }, [sdk, isLoggedIn]);

  useEffect(() => {
    const pDataid = query.get("senarioId") || "";
    const value = query.get("value") || "";
    const pDemo = query.get("demo");

    setDemo(pDemo || "");
    if (sdk) {
      if (pDataid != "") {
        onUserChange({ scenId: pDataid, auto: true });
      }

      readUserState();
      focusInput();
    }

    setListMode(value);
  }, [sdk, mappedWordList]);

  useEffect(() => {
    const handleNextStep = async () => {
      let sstep = step + 1;
      if (!success && !fail && step !== 0 && !pass) {
        setStep(sstep);

        const nextWord = exerciseWords[sstep - 1];

        await fetchSpeech(nextWord);
        setWordValue("");
      }
    };

    handleNextStep();
  }, [success, fail, pass]);

  const fetchSpeech = async (wordItem: WordItem) => {
    setAudioPath("");
    try {
      if (sdk && wordItem) {
        const ttsWord = wordItem.phrase || wordItem.word;

        const result = await sdk.getTTS().get(ttsWord, "fr");
        if (result && result.data && result.data.success && result.data.url) {
          setAudioPath(result.data.url);
        }
      }
    } catch (error) {
      console.log("fetcherror", error);
    }
  };

  const onUserChange = async ({
                                scenId,
                                auto,
                              }: {
    scenId: string;
    auto?: boolean;
  }) => {
    if (!sdk) {
      setError({
        id: "sdk-not-initialized",
      });
      return;
    }

    if (scenId == "") {
      setError({
        id: "empty-scenario-id",
      });
      return;
    }
    setLoading(true);
    const guestScenario = scenId.includes("guest");

    const data: SquidexScenarioData = guestScenario ?
      JSON.parse(sessionStorage.getItem(guestScenariosStorageKey) || "[]").find((scenario: SquidexScenarioData) => scenario.id === scenId) :
      await sdk.getDataManager().getScenario(scenId);

    setLoading(false);
    setCorrectWords([]);
    setPassedWords([]);

    if (!data) {
      setError({
        id: "empty-scenario-data",
      });
      return;
    }
    scenarioAuthor.current = data.AUTHOR_ID;
    setTags(data.tags);
    setName(data.name);
    const currentScenario = new VocabScenario({
      id: "",
      name: data.name,
      tags: data.tags,
      wordsList: resolveWordList(mappedIdWordList, data.wordsList)
    })

    if (data.wordsList && data.id) {
      //setWords(data.wordsList);
      setExerciseWords(currentScenario.wordsList);

      setInitialWordsLength(currentScenario.wordsList.length);

      if (!data.id.includes("guest_")) {
        currentScenario.id = data.id;
        setScenarioId(data.id)
      }

      setWordcode(data.id);
      setProgress(0);

      setPreview(true);
    } else {
      setExerciseWords([]);
      setScenarioId("");
      setWordcode("");

      setPreview(true);
    }
    setCurrentScenario(currentScenario);
  };

  const onStart = (scenId?: string) => {
    onUserChange({ scenId: scenId ? scenId : scenarioId });
  };

  const updateUserStats = async () => {
    try {
      if (sdk && isLoggedIn) {
        await sdk.getDataManager().saveSettings({
          data: {
            annualExerciseCount: annualExerciseCount + 1,
            annualWordList: annualWordList,
          }
        });
        setAnnualExerciseCount(prevState => prevState + 1);
      }
    } catch (err) {
      console.log(err);
    }
    if (sdk && !ignoreStats) {
      await sdk.getSessionManager().saveSessionData(
        {
          data: {
            sessionExerciseCount: sessionExerciseCount + 1,
            sessionWordList: sessionWordList
          }
        }
      )
      setSessionExerciseCount(prevState => prevState + 1);
    }
  };

  const readUserState = async () => {


    return;
    //TODO: handle restoring data from the URL, Now its not working at all
    // if (sdk) {
    //   const state = await sdk.getDataManager().readState();
    //   if (state) {
    //     setStep(state.step);
    //     setCorrect(state.correct);
    //     setCorrectWords(state.correctWords);
    //     setPassedWords(state.passedWords);
    //   }
    // }
  };

  const writeUserState = async (
    sstep: any,
    ccorrect: any,
    correctWords: any,
    passedWords: any
  ) => {
    if (sdk) {
      await sdk.getDataManager().writeState({
        step: sstep,
        correct: ccorrect,
        correctWords: correctWords,
        passedWords: passedWords,
      });
    }
  };

  const complete = () => {
    setCorrect(0);
    setResult(MAINSCREEN_RESULT_STEPS.INITIAL);
    setWordValue("");
    setWrongWords([]);
    setCorrectWords([]);
    setPassedWords([]);
    setProgress(0);
    setStep(0);
    setCurrentStep(1);
    setWrongPassed(false);
    setIgnoreStats(false);
  };

  const updateAnnualWordList = (id: string) => {
    if (annualWordList.indexOf(id) === -1) {
      setAnnualWordList(annualWordList.concat(id));
    }
  };
  const updateSessionWordList = (id: string) => {
    if (sessionWordList.indexOf(id) === -1) {
      setSessionWordList(sessionWordList.concat(id));
    }
  };

  const onValidate = async (
    wordlist: VocabWord[],
    id: string,
    auto?: boolean,
    inputValue?: string,
  ) => {
    const value = inputValue || wordValue;
    inputValue && setWordValue(inputValue);
    let tempStep = step,
      tempCorrect = correct;
    if (!sdk || !wordlist || wordlist.length === 0) {
      return;
    }

    if (step === 0) {
      tempCorrect = 0;
      setWrongPassed(false);
      setResult(MAINSCREEN_RESULT_STEPS.INITIAL);
      if (auto || scenarioId === id) {
        tempStep += 1;
        setStep(tempStep);
        // setCorrect(1);
        setCorrect(0);
        setProgress(0);

        await fetchSpeech(wordlist[tempStep - 1]);
        setCorrectWords([]);
        setWrongWords([]);
        setPassedWords([]);
        await saveToMyList(sdk, scenarioId);
      } else {
        setError({ id: "wrong-scenario-id" });
      }
    } else if (step > 0) {
      tempStep = step;
      const currentWord: VocabWord = wordlist[tempStep - 1];

      if (value.trim() === "") {
        return;
      }

      const areCapitalsCorrect = (word: string, input: string): boolean => {
        const wordArray: string[] = word.split('');
        const inputArray: string[] = input.split('');
        for (let i = 0; i < wordArray.length; i++) {
          if (wordArray[i].toUpperCase() === wordArray[i] && wordArray[i] !== inputArray[i]) return false
        }
        return true
      }

      const handleError = () => {
        setDisplayWord(value);

        setWrongWords(
          wrongWords.indexOf(wordlist[tempStep - 1]) === -1
            ? wrongWords.concat([wordlist[tempStep - 1]])
            : wrongWords
        );
        currentStep < initialWordsLength && setCurrentStep(currentStep + 1);
        setProgress(tempStep / initialWordsLength * 100);
        updateSessionWordList(currentWord.id);
        setFail(true);
      }

      if (!areCapitalsCorrect(currentWord.word, value.trim())) handleError();
      else if (
        value.trim() === currentWord.word ||
        currentWord.words.some((validAnswer) => validAnswer === value.trim())
      ) {
        setDisplayWord(value);

        tempCorrect += 1;
        currentStep < initialWordsLength && setCurrentStep(currentStep + 1);
        setProgress(tempStep / initialWordsLength * 100)

        setCorrectWords(correctWords.concat([currentWord]));
        updateAnnualWordList(currentWord.id);
        updateSessionWordList(currentWord.id);
        setSuccess(true);
        if (wrongPassed) {
          //compare and remove correct answered item from wrong word list
          setWrongWords(
            wrongWords.filter((wordItem) => {
              return (
                wordItem.word.toLowerCase() !== currentWord.word.toLowerCase()
              );
            })
          );
        }

        setCorrect(tempCorrect);
      } else if (
        value.trim().toLowerCase() === currentWord.word.toLowerCase() ||
        currentWord.words.some(
          (validAnswer) =>
            validAnswer.toLocaleLowerCase() === value.trim().toLowerCase()
        )
      ) {
        setWarning(true);
      } else handleError()
    }

    setError(null);
  };

  useEffect(() => {
    if (currentScenario && step > 0) {
      writeUserState(step, correct,
        correctWords.map(word => ({ id: word.id })),
        passedWords.map(word => ({ id: word.id })));
    }
  }, [step, correctWords, passedWords]);

  const onPass = useCallback(
    (currentWord: VocabWord) => {
      let tempStep = step;
      if (!exerciseWords || exerciseWords.length === 0) {
        return;
      }

      if (step > 0) {
        setPassedWords(
          passedWords.findIndex(
            (wordItem) => wordItem.id === currentWord.id
          ) === -1
            ? passedWords.concat([currentWord])
            : passedWords
        );

        setPass(true);
      }
    },
    [step, passedWords, wrongWords]
  );

  const focusInput = () => {
    const input = document.getElementById("word-input");
    if (input) {
      input.focus();
    }
  };

  const onNext = async (status: string) => {
    let sstep = step + 1;

    if (status === "pass") {
      setWordValue("");
    }

    if (sstep > exerciseWords.length) {
      if (!wrongPassed && passedWords.length > 0) {
        setExerciseWords(exerciseWords.concat(passedWords));
        setWrongPassed(true);

        const nextWord = passedWords[0];
        await fetchSpeech(nextWord);
      } else {
        sstep = 0;
        setResult(MAINSCREEN_RESULT_STEPS.COMPLETE);
        logExerciseAnalyticsEvent("complète");
        await updateUserStats();
      }
    }
  };

  const handleExit = async () => {
    setStep(0);
    setCurrentStep(1);
    setExitExercise(false);
    logExerciseAnalyticsEvent("quite");
  };

  const backToIntialStep = () => {
    setStep(0);
    setListMode("");
  };
  const goToListMode = (value: string) => {
    setStep(0);
    setListMode(value);
  };

  const gotoScenarioList = () => {
    navigate("/myexercises", { replace: true });
  };

  const handleStartFromPreview = async (scenario: string | VocabWord[], restart?: boolean) => {
    if (typeof scenario === "string") {

    } else {
      setRestartMode(false);
      const shuffledWords = [...scenario].sort(() => 0.5 - Math.random());
      setExerciseWords(shuffledWords);
      setInitialWordsLength(shuffledWords.length);
      setPreview(false);
      setListMode("");
      logExerciseAnalyticsEvent("débute");
      onValidate(shuffledWords, scenarioId, true);
      if (restart) await fetchSpeech(shuffledWords[0]);
    }
  };

  const handleQuit = () => {
    setResult(MAINSCREEN_RESULT_STEPS.RESULT);
  };

  const handleRestart = (wrongWords: VocabWord[]) => {
    logExerciseAnalyticsEvent("réessaie");
    setRestartMode(true);
    complete();
    setStep(1);
    setCurrentStep(1);
    handleStartFromPreview(wrongWords, true);
    setIgnoreStats(true);
  };

  const sortCorrectnessArray = (array: VocabWord[]): VocabWord[] => {
    return currentScenario?.wordsList.filter(word => array.includes(word)) || []
  }

  const logExerciseAnalyticsEvent = (actionName: string) => {
    if (sdk) {
      sdk.getAnalytics().push({
        event: "liste",
        action: actionName,
        id: scenarioId,
        level: listMode,
        selectionne: exerciseWords?.length,
        total: scenarioWords.current?.length,
        reussie: correctWords?.length,
        type: scenarioAuthor.current === 'squidex' ?
          'ap' : scenarioAuthor.current === 'alloprof' ? 'magimot' : 'perso',
        platform: process.env.REACT_APP_PLATFORM
      })
    }
  }

  const resultModal = useMemo(() => {
    return (
      <APResultDialog
        preventCloseOnEsc
        open={result === MAINSCREEN_RESULT_STEPS.RESULT}
        title={getFormattedString('bravo-heading')}
        onNext={complete}
      >
        <Text fontSize={["lg", "xl"]} lineHeight={[1.25, 1.5]} marginBottom={["0.5rem", "0.75rem"]}
              textAlign={"center"}>
          <APFormattedMessage id={"words-added-to-vocabulary-title"} values={{ correct }} />
        </Text>
        <Grid templateColumns={`repeat(2, 1fr)`} gap={[2, 3, 4]} width={"100%"}>

          <StatBox value={sessionWordList.length}
                   translationId={"successful-words-in-this-session"}
                   backgroundColor={"lightGray.500"} />

          <StatBox value={sessionExerciseCount}
                   translationId={"word-lists-revised-in-this-session"}
                   backgroundColor={"lightGray.500"} />

          {isLoggedIn && (<>
            <StatBox value={annualWordList.length}
                     translationId={"mastered-words-this-year"}
                     backgroundColor={"lightGray.500"} />

            <StatBox value={annualExerciseCount}
                     translationId={"word-lists-revised-this-year"}
                     backgroundColor={"lightGray.500"} />
          </>)}
        </Grid>


      </APResultDialog>
    )
  }, [annualExerciseCount, annualWordList, isLoggedIn, sessionWordList, sessionExerciseCount, result, complete])

  return (
    <>
      <Profile sdk={sdk} sessionData={{
        sessionWordList: sessionWordList,
        sessionExerciseCount: sessionExerciseCount
      }}
      />
      <Flex
        className={"main-content"}
        direction={"column"}
        bgColor={skin?.data?.color?.iv}
        bgImage={skin?.data?.background ? `url(${skin.data.background})` : `var(--ap-theme-default)`}
        bgPosition={"center"}
        bgSize={"cover"}
        bgRepeat={"no-repeat"}
      >
        {(listMode === "" && step !== 0) ?
          <>
            <ExerciseScreen
              key={"exerciseScreen"}
              onValidate={onValidate}
              words={exerciseWords}
              name={name}
              initialWordsLength={initialWordsLength}
              scenarioId={scenarioId}
              step={step}
              currentStep={currentStep}
              setExitExercise={setExitExercise}
              audioPath={audioPath}
              progress={progress}
              correct={correct}
              wordValue={wordValue}
              setWordValue={setWordValue}
              error={error}
              setError={setError}
              onPass={onPass}
              success={success}
              warning={warning}
              fail={fail}
              pass={pass}
            />
            <APCommonDialog
              open={exitExercise}
              title={getFormattedString('quit-exercise-title')}
              onNext={handleExit}
              onClose={() => setExitExercise(false)}
              error={true}
            >
              <Box textAlign={"center"}>
                <TextWithLineBreaks text={getFormattedString('quit-exercise-description')} />
              </Box>
            </APCommonDialog>

            <APCompleteDialog
              open={result === MAINSCREEN_RESULT_STEPS.COMPLETE}
              onNext={() => setResult(MAINSCREEN_RESULT_STEPS.CORRECTNESS)}
            />
            <CorrectnessModal
              open={result === MAINSCREEN_RESULT_STEPS.CORRECTNESS}
              onRestart={handleRestart}
              onQuit={handleQuit}
              correctWords={sortCorrectnessArray(correctWords)}
              wrongWords={sortCorrectnessArray(wrongWords)}
              passedWords={sortCorrectnessArray(passedWords)}
            />
            <ValidationStatus
              key={"exerciseValidation"}
              open={success || warning || fail || pass}
              onNext={warning ? () => setWarning(false) : onNext}
              clearStatus={() => {
                setSuccess(false);
                setFail(false);
                setPass(false);
                setWarning(false);
              }}
              word={step > 0 ? exerciseWords[step - 1] : undefined}
              currentWord={displayWord}
              status={success ? "success" : warning ? "warning" : pass ? "pass" : fail ? "fail" : "none"}
              clearWordValue={() => !warning && setWordValue("")}
            />
            {resultModal}
          </> :
          <>
            {listMode !== "" && (
              <ScenarioListScreen
                sdk={sdk}
                backToIntialStep={backToIntialStep}
                onStart={onStart}
              />
            )}
            {listMode === "" && step === 0 && (
              <InitialStep
                sdk={sdk}
                onStart={onStart}
                demo={demo}
                goToListMode={goToListMode}
              />
            )}
            <ScenarioPreviewModal
              allowChecks
              open={preview}
              scenario={currentScenario}
              listDescription={<APFormattedMessage id={"words-of-exercise"} />}
              onStart={handleStartFromPreview}
              onClose={() => setPreview(false)}
              sdk={sdk}
            />
          </>
        }
      </Flex>
    </>
  );
}

export default MainScreen;
